'use client';

import { Video } from '@app/_components/Video';
import PlayIcon from '@app/_icons/PlayIcon/PlayIcon';
import Image from '@components/Image';
import { useEffect, useRef } from 'react';
import type { Image as IImage } from 'types/image';

import { FEATURE_FLAGS } from '@/config/featureFlags';
import { cn } from '@/lib/utils';

const isFullyVisible = (el: HTMLElement, container: HTMLElement) => {
    const { bottom, top, left, right } = el?.getBoundingClientRect() || {};
    const containerRect = container?.getBoundingClientRect() || {};

    return (
        left >= containerRect.left &&
        right <= containerRect.right &&
        top >= containerRect.top &&
        bottom <= containerRect.bottom
    );
};

/**
 * @deprecated - Deprecated in favor of ProductPageGalleryThumbnails.tsx
 */
export default function Thumbnails({
    goToItem,
    activeIndex,
    images,
    className,
}: {
    goToItem: (index: number, smooth?: boolean) => void;
    activeIndex: number;
    images: IImage[];
    className?: string;
}) {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!ref.current) return;

        const currentChild = ref.current?.children[
            activeIndex
        ] as HTMLDivElement;

        if (isFullyVisible(currentChild, ref.current as HTMLDivElement)) return;

        ref?.current?.scrollTo({
            top: currentChild?.offsetTop,
            left: currentChild?.offsetLeft,
            behavior: 'smooth',
        });
    }, [activeIndex]);

    return (
        <div ref={ref} className={cn('flex gap-2 scrollbar-none', className)}>
            {images.map((media, i) => {
                if (!media?.src) {
                    return null;
                }

                return (
                    <div
                        key={`${media.id}-${String(i)}`}
                        className={cn(
                            'relative h-12 w-12 flex-shrink-0',
                            i === activeIndex && 'ring-1 ring-inset ring-black',
                        )}
                        role="button"
                        tabIndex={0}
                        onClick={() => goToItem(i, false)}>
                        {media.type === 'image' && (
                            <Image
                                src={media.src}
                                alt={media.alt}
                                widths={[96]}
                                className={cn(
                                    'h-full w-full object-cover mix-blend-multiply',
                                )}
                            />
                        )}
                        {media.type === 'video' &&
                            FEATURE_FLAGS.products.videos && (
                                <>
                                    <PlayIcon className="absolute left-1/2 top-1/2 h-em -translate-x-1/2 -translate-y-1/2 rounded-full bg-black/30 text-xl text-white" />
                                    <Video src={media.src} autoPlay={false} />
                                </>
                            )}
                    </div>
                );
            })}
        </div>
    );
}
